import { useState } from 'react'
import { Alert, Button, Code, Group, Stack, Text } from '@mantine/core'
import { IconFileText } from '@tabler/icons-react'

import { disableWafLogging, enableWafLogging, type WafLoggingResponse } from '@/api/waf'
import { formatApiError } from '@/api/errors'
import { useCan } from '@/store/auth'

/**
 * Card do log de tráfego, nos três estados que ele realmente tem.
 *
 * Existe porque **vazio se lê como defeito**. Antes desta fatia, quem pesquisava log
 * numa WAF sem entrega configurada recebia "Logs Insights indisponível — configure o
 * logging no AWS WAF Console": uma instrução para sair do produto e fazer à mão.
 *
 * Os três estados, e o que cada um responde:
 *
 * - **desligado** → "não há log porque ninguém ligou", com o botão. Log de WAF custa
 *   por GB ingerido, e a escolha é do operador (decisão de Carlos, 12/08) — o
 *   provisionamento não liga nada;
 * - **sem capacidade** → "a role desta conta não pode ligar", **nomeando as ações que
 *   faltam**. É isso que o operador leva a quem administra a conta AWS;
 * - **ligado** → o nome do grupo, para quem for investigar no CloudWatch, e o botao de
 *   DESLIGAR. Ate 14/08/2026 este estado existia escrito aqui e **nunca renderizava**:
 *   nada entregava `logGroup` ao componente, entao ligar o log era porta de mao unica no
 *   produto — o desligamento saia por `aws wafv2 delete-logging-configuration`. A rota
 *   `DELETE /logging` ja existia, com auditoria e preservando o grupo; faltava so o
 *   controle.
 */
export interface WafLoggingCardProps {
  wafId: number
  /** Grupo resolvido ao vivo pelo backend; `null`/ausente = log desligado. */
  logGroup?: string | null
  /** Chamado depois de ligar com sucesso, para a tela refazer a busca. */
  onEnabled?: () => void
  /** Chamado depois de desligar com sucesso, para a tela refazer a busca. */
  onDisabled?: () => void
}

export function WafLoggingCard({ wafId, logGroup, onEnabled, onDisabled }: WafLoggingCardProps) {
  // A rota `POST /logging` exige `waf:manage`. Sem este gate, quem tem apenas
  // `waf:view` via o botao e tomava 403 ao clicar — o mesmo defeito que a 1.60.229
  // consertou na GMUD ("via os mesmos botoes de acao e tomava erro do servidor").
  // Achado pelo Playwright: nem o teste de unidade nem o de backend pegavam, porque
  // e permissao no contexto da pagina real.
  const can = useCan()
  const podeGerenciar = can('waf', 'manage')
  const [ligando, setLigando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [bloqueio, setBloqueio] = useState<WafLoggingResponse | null>(null)
  const [confirmando, setConfirmando] = useState(false)
  const [desligando, setDesligando] = useState(false)

  async function desligar() {
    setErro(null)
    setDesligando(true)
    try {
      await disableWafLogging(wafId)
      setConfirmando(false)
      onDisabled?.()
    } catch (e) {
      // `WAFNonexistentItemException` = ja estava desligado. Idempotente, nao erro —
      // e a MESMA excecao que em 13/08 virava 500 na aba de regras (Fase 2 do plano
      // dos consertos do teste de WAF).
      const msg = formatApiError(e, 'Não foi possível desligar o log.')
      if (/nonexistent/i.test(msg)) {
        setConfirmando(false)
        onDisabled?.()
      } else {
        setErro(msg)
      }
    } finally {
      setDesligando(false)
    }
  }

  if (logGroup) {
    return (
      <Alert color="green" icon={<IconFileText size={16} />} title="Log habilitado">
        <Stack gap="xs">
          <Text size="sm">
            Gravando em <Code>{logGroup}</Code>. Apenas requisições que casaram regra
            entram no log — tráfego limpo é descartado para conter o custo.
          </Text>
          {erro && <Text size="sm" c="red">{erro}</Text>}
          {podeGerenciar && (
            <Group>
              {!confirmando ? (
                <Button size="xs" variant="light" color="red" onClick={() => setConfirmando(true)}>
                  Desligar log
                </Button>
              ) : (
                <>
                  {/* A confirmacao diz o que se PERDE, nao "tem certeza?". Ligar custa
                      dinheiro; desligar custa visibilidade numa WAF de producao, e as
                      duas coisas nao merecem o mesmo peso de interface. */}
                  <Text size="sm">
                    Para de gravar novas requisições. O grupo e o que já foi gravado
                    ficam, até a retenção expirar.
                  </Text>
                  <Button size="xs" color="red" onClick={desligar} loading={desligando}>
                    Confirmar desligamento
                  </Button>
                  <Button size="xs" variant="subtle" onClick={() => setConfirmando(false)}>
                    Cancelar
                  </Button>
                </>
              )}
            </Group>
          )}
        </Stack>
      </Alert>
    )
  }

  // `blocked` não é erro de execução: a conta não tem as permissões. Separado do
  // `erro` de propósito, porque a ação do operador é diferente — aqui ele precisa
  // pedir permissão, não tentar de novo.
  if (bloqueio?.status === 'blocked') {
    return (
      <Alert color="yellow" icon={<IconFileText size={16} />} title="Sem permissão para ligar o log">
        <Stack gap="xs">
          <Text size="sm">{bloqueio.motivo}</Text>
          {!!bloqueio.missing?.length && (
            <Text size="sm">
              Falta na role desta conta: <Code>{bloqueio.missing.join(', ')}</Code>
            </Text>
          )}
        </Stack>
      </Alert>
    )
  }

  const ligar = async () => {
    setLigando(true)
    setErro(null)
    try {
      const res = await enableWafLogging(wafId)
      if (res.status === 'blocked') {
        setBloqueio(res)
        return
      }
      onEnabled?.()
    } catch (e) {
      setErro(formatApiError(e, 'Não foi possível ligar o log.'))
    } finally {
      setLigando(false)
    }
  }

  return (
    <Alert color="yellow" icon={<IconFileText size={16} />} title="Log desligado">
      <Stack gap="xs">
        <Text size="sm">
          Esta Web ACL não está gravando log de tráfego, então não há o que pesquisar.
          Ligar grava em CloudWatch Logs apenas o que casou regra, com retenção de 30
          dias — log de WAF é cobrado por GB ingerido.
        </Text>
        {erro && <Text size="sm" c="red">{erro}</Text>}
        {podeGerenciar ? (
          <Group>
            <Button size="xs" onClick={ligar} loading={ligando}>
              Ligar log
            </Button>
          </Group>
        ) : (
          <Text size="sm" c="dimmed">
            Ligar o log exige permissão de gerenciar WAF. Fale com quem administra
            seus acessos.
          </Text>
        )}
      </Stack>
    </Alert>
  )
}
